import { HSpacer, ProgressLoader, Text, VSpacer } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useReportGeneration } from '@/pages/Reports/useReportGeneration';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { Box, Stack } from '@mui/material';
import { ReportStatus } from '@shared/enums';
import { ReportEndpoint } from '@api/endpoints';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ExportReportDialogProps {
  /** Extra controls shown under the confirm message while the export has not started yet. */
  children?: React.ReactNode,
  confirmMessage: string,
  isConfirmDisabled?: boolean,
  onClose: () => void,
  request: ReportEndpoint.Create.Request,
  retailerId: string,
  testID?: string,
  title?: string,
}

export const ExportReportDialog = ({
  children,
  confirmMessage,
  isConfirmDisabled,
  onClose,
  request,
  retailerId,
  testID = 'export-confirmation-dialog',
  title,
}: ExportReportDialogProps) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const notifyFailed = () => openSnackbar(t($ => $.reports.generation.failedSnackbar));
  const { clearReport, download, isGenerating, report, requestReport } = useReportGeneration(
    retailerId, notifyFailed,
  );

  const isCompleted = report?.status === ReportStatus.Completed;
  const isFailed = report?.status === ReportStatus.Failed;

  const getConfirmText = () => {
    if (isCompleted) {
      return t($ => $.reports.generation.downloadButton);
    }
    if (isFailed) {
      return t($ => $.reports.generation.tryAgain);
    }
    return t($ => $.common.actions.export);
  };

  const getBody = () => {
    if (isGenerating) {
      return (
        <Stack alignItems="center" direction="row">
          <Box alignItems="center" display="flex" height={28} justifyContent="center" width={28}>
            <ProgressLoader size={20} type="circular" />
          </Box>
          <HSpacer size="4" />
          <Text category="body-large">{t($ => $.reports.generation.generating)}</Text>
        </Stack>
      );
    }
    if (isCompleted) {
      return t($ => $.reports.generation.ready);
    }
    if (isFailed) {
      return report?.errorMessage || t($ => $.reports.generation.failed);
    }
    if (!children) {
      return confirmMessage;
    }
    return (
      <Stack>
        <Text category="body-large">{confirmMessage}</Text>
        <VSpacer size="5" />
        {children}
      </Stack>
    );
  };

  const handleConfirm = async () => {
    if (isCompleted) {
      await download().catch(notifyFailed);
      return;
    }
    clearReport();
    await requestReport(request).catch(notifyFailed);
  };

  return (
    <ConfirmDialog
      cancelText={t($ => (
        isCompleted || isGenerating ? $.common.actions.close : $.common.actions.goBack
      ))}
      confirmText={getConfirmText()}
      isConfirmDisabled={isConfirmDisabled}
      onCancel={onClose}
      onConfirm={isGenerating ? undefined : handleConfirm}
      open
      showCloseButton={false}
      testID={testID}
      title={title}
    >
      {getBody()}
    </ConfirmDialog>
  );
};
